import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TimelineContent, Dot } from '@components/Timeline';
import InfoText from './InfoText';
import ProductTypes from './ProductTypes';
import Toolbar from './Toolbar';
import RateTable from './RateTable';
import CalculatorMessages from './CalculatorMessages';
import LoadMoreRates from './LoadMoreRates';
import RateDisclosures from './RateDisclosures';
import RateModal from './RateModal';
import RackRates from './RackRates';
import BottomContent from './BottomContent';
import DashedLine from '@components/DashedLine';
import usePrevious from '@hooks/usePrevious';
import useWindowResizing from '@hooks/useWindowResizing';
import useDocumentReady from '@hooks/useDocumentReady';
import useIsMobile from '@hooks/useIsMobile';
import useMatchMedia from '@hooks/useMatchMedia';
import * as styles from './Content.module.scss';
import classNames from 'classnames';
import { getElementsToTransform, isMobileDevice } from '@helpers';
import { sortStates } from '@enums';
import { orderBy } from 'lodash';
import { useSpring, animated } from 'react-spring';

export default React.memo(function Content({
  loanDetails,
  inputsRect,
  inputsAreOpen,
  productTypes,
  hasEnabledProductTypes,
  rates,
  effectiveDate,
  isLoading,
  isLoadingCategory,
  canLoadMore,
  hasInitialData,
  errors,
  showRackRates,
  rackRateGroups,
  rackRateLoanPurpose,
  rackRateLoanType,
  rackRatesAreLoading,
  rackRatesError,
  ctas,
  faqs,
  toggleProductCategory,
  toggleProductType,
  loadMore,
  fetchRackRateGroups
}) {
  //////////////////////////
  // User Interface
  //////////////////////////

  const isMobile = useIsMobile();

  const style = useMemo(() => {
    const height = inputsRect?.height;
    const hasRect = !!height;
    const property = val => {
      return isMobile || !hasRect ? null : val;
    };

    return {
      height: property(height),
      marginTop: property(-height),
      y: property(height),
      transform: property(`translate3d(0, ${height}px, 0)`)
    };
  }, [inputsRect, isMobile]);

  const isAnimating = useRef(false);
  const isResizing = useWindowResizing();

  const prevLoanPurpose = useRef(loanDetails?.loanPurpose);
  const isSwitchingLoanPurpose = useMemo(() => {
    const currentLoanPurpose = prevLoanPurpose.current;
    const newLoanPurpose = loanDetails?.loanPurpose;
    prevLoanPurpose.current = newLoanPurpose;

    return inputsAreOpen && currentLoanPurpose !== newLoanPurpose;
  }, [loanDetails?.loanPurpose, inputsAreOpen]);

  const spring = useSpring({
    y: isMobile || showRackRates || !inputsAreOpen ? 0 : style.y,
    cancel: isMobile || showRackRates,
    immediate: isResizing || isSwitchingLoanPurpose,
    config: {
      tension: inputsAreOpen || isAnimating.current ? 165 : 120
    },
    delay: () =>
      isMobile || showRackRates || inputsAreOpen || isAnimating.current
        ? 0
        : 260,
    onStart: () => (isAnimating.current = true),
    onRest: () => (isAnimating.current = false),
    onChange: transformElements
  });

  const springRef = useRef(spring);

  useEffect(() => {
    springRef.current = spring;
  }, [spring]);

  useEffect(() => {
    if (isMobile || showRackRates) {
      springRef.current.y?.set(0);
    }
  }, [isMobile, showRackRates]);

  // Ensure transform elements are
  // queried before animation starts
  useDocumentReady(getElementsToTransform);

  const toolbarOffset = useMemo(() => {
    return !inputsAreOpen ? 0 : (inputsRect?.height ?? 0);
  }, [inputsAreOpen, inputsRect]);

  //////////////////////////
  // Rates
  //////////////////////////

  const [sortState, setSortState] = useState(sortStates.PRODUCT.value);

  const prevRates = usePrevious(rates);
  const currentRates = useMemo(() => {
    const currentRates = isLoading && !hasInitialData ? prevRates : rates;
    const order = Object.values(sortStates).find(
      ({ value }) => value === sortState.value
    )?.order;

    return orderBy(
      currentRates,
      sortState.split(/, */),
      order?.split(/, */) ?? 'asc'
    );
  }, [isLoading, rates, prevRates, hasInitialData, sortState]);

  const hasRates = !!currentRates?.length;
  const hasErrors = !!errors?.general;
  const disableInteraction =
    hasErrors || !hasInitialData || !hasEnabledProductTypes || !hasRates;

  // The product displayed in the sample loan
  // and sent with the modal CTA form
  const [modalProduct, setModalProduct] = useState(null);

  useEffect(() => {
    setModalProduct(null);
  }, [loanDetails, showRackRates]);

  const loanPurposeLabel = (
    showRackRates ? rackRateLoanPurpose : (loanDetails?.loanPurpose ?? null)
  )?.toLowerCase();

  const productTypesLabel = useMatchMedia(
    530,
    'Select mortgage loan types to compare',
    'Select the types of mortgage loans you would like to compare'
  );

  return (
    <>
      <animated.div
        className={styles.whiteBg}
        style={{
          ...spring,
          height: style.height,
          marginTop: -style.height
        }}
      />
      <animated.div
        id="calculator-content"
        className={classNames(styles.wrapper, {
          loading: isLoading && !hasInitialData,
          'no-initial-data': !hasInitialData,
          'no-products': !hasEnabledProductTypes
        })}
        style={{
          ...spring,
          marginTop: style.marginTop
        }}
      >
        <section>
          <div className="content-wrapper">
            <div className={styles.content}>
              <TimelineContent>
                <Dot className={styles.dot} number={3} />
                <div className={styles.text}>
                  <h2>
                    Compare your{' '}
                    <span className="highlight-term">{loanPurposeLabel}</span>{' '}
                    mortgage&nbsp;rates
                  </h2>
                </div>
              </TimelineContent>
              {!showRackRates && (
                <TimelineContent className={styles.productTypesWrapper}>
                  <DashedLine
                    direction="horizontal"
                    strokeWidth={1.5}
                    className={styles.productsDivider}
                  />
                  <InfoText
                    id="product-types-label"
                    className={styles.productTypesLabel}
                    icon="filter"
                  >
                    {productTypesLabel}
                  </InfoText>
                  <ProductTypes
                    productTypes={productTypes}
                    className={styles.productTypes}
                    categoryWrapperClassName={
                      styles.productTypesCategoryWrapper
                    }
                    categoryButtonClassName={styles.productTypesCategory}
                    isLoading={isLoading}
                    isLoadingCategory={isLoadingCategory}
                    hasErrors={hasErrors}
                    toggleProductCategory={toggleProductCategory}
                    toggleProductType={toggleProductType}
                  />
                  {!hasErrors && (
                    <DashedLine
                      direction="horizontal"
                      strokeWidth={1.5}
                      className={styles.productsDivider}
                    />
                  )}
                </TimelineContent>
              )}
            </div>
            {(!hasErrors || (showRackRates && !rackRatesAreLoading)) && (
              <TimelineContent className={styles.rateTableLabelWrapper}>
                <InfoText
                  id="rate-table-label"
                  className={styles.rateTableLabel}
                  icon="details"
                >
                  {isMobileDevice() ? 'Tap' : 'Click'} a row to see detailed
                  information about that loan product and rate
                </InfoText>
              </TimelineContent>
            )}
          </div>
          <div id="calculator-rates" className={styles.rates}>
            {!showRackRates ? (
              <>
                {!hasErrors && (
                  <>
                    <Toolbar
                      offset={toolbarOffset}
                      sort={sortState}
                      setSort={setSortState}
                      productTypes={productTypes}
                      isLoading={isLoading}
                      isLoadingCategory={isLoadingCategory}
                      isDisabled={disableInteraction}
                      isStatic={(!hasRates && hasInitialData) || hasErrors}
                      toggleProductCategory={toggleProductCategory}
                      toggleProductType={toggleProductType}
                    />
                    <RateTable
                      rates={currentRates}
                      isLoading={isLoading}
                      hasInitialData={hasInitialData}
                      setModalProduct={setModalProduct}
                    />
                  </>
                )}
              </>
            ) : (
              <RackRates
                groups={rackRateGroups}
                currentLoanPurpose={rackRateLoanPurpose}
                currentLoanType={rackRateLoanType}
                rackRatesAreLoading={rackRatesAreLoading}
                rackRatesError={rackRatesError}
                setModalProduct={setModalProduct}
              />
            )}
          </div>
          {!showRackRates && (
            <>
              <div className={styles.bottom}>
                <CalculatorMessages
                  errors={errors}
                  noProductsSelected={!hasEnabledProductTypes}
                  noRatesFound={!hasRates && !isLoading}
                  fetchRackRateGroups={fetchRackRateGroups}
                />
                <LoadMoreRates
                  forceHide={hasErrors || (hasInitialData && !hasRates)}
                  hasInitialData={hasInitialData}
                  errors={errors}
                  canLoadMore={canLoadMore}
                  isLoading={isLoading}
                  loadMore={loadMore}
                />
                {hasRates && !hasErrors && (
                  <RateDisclosures effectiveDate={effectiveDate} />
                )}
              </div>
            </>
          )}
          <RateModal
            id="calculator-rate-modal"
            product={modalProduct}
            loanDetails={loanDetails}
            isRackRate={showRackRates}
            setProduct={setModalProduct}
          />
        </section>
        <BottomContent ctas={ctas} faqs={faqs} />
      </animated.div>
    </>
  );
});

function transformElements({ value }) {
  const transform = `translate3d(0, ${value.y}px, 0)`;

  getElementsToTransform().forEach(el => {
    el.style.transform = transform;
  });
}
